import type { Color, CzmlDataSource, Entity } from 'cesium';
import cloneDeep from 'lodash.clonedeep';
import { writable, derived, type Readable, get } from 'svelte/store';
import { mapLayersStore } from './map-layers-store';
import { projectAndPlanStatus } from '$lib/utils/constants';
import { mapToolsStore } from './map-tools-store';

export interface IMapModeStore {
	mode: 'LIVE' | 'SANDBOX';
	expandableProjectPickerCurrentView: // Minimized in map
	| 'MINIMIZED'
		// Overview of projects
		| 'PROJECTS'
		// Information screen for single project
		| 'PROJECT_INFO'
		// Step 1 of project creation
		| 'POLYGON_DRAWING_PROJECT_CREATION'
		// Step 2 of project creation
		| 'CREATE_PROJECT'
		// Edit project screen
		| 'EDIT_PROJECT_DETAILS'
		// Drawing enabled in project editing
		| 'POLYGON_DRAWING_PROJECT_EDITING'
		// Drawing enabled in plan creation
		| 'POLYGON_DRAWING_PLAN_CREATION'
		// Drawing enabled in plan editing
		| 'POLYGON_DRAWING_PLAN_EDITING'
		// Form enabled in plan creation
		| 'CREATE_PLAN_DETAILS'
		// View plan
		| 'VIEW_PLAN_DETAILS'
		// Edit plan screen
		| 'EDIT_PLAN_DETAILS';
	newProjectPolygonEntity: Entity | null;
	newPlanPolygonEntity: Entity | null;
	selectedProjectAndPlan: {
		project: {
			id?: number;
			name?: string;
		};
		plan: {
			id?: number;
			name?: string;
		};
	};
	settings: {
		planPolygonOpacity: number,
		projectPolygonOpacity: number,
		entityArea: number
	}
	activeDataSource?: CzmlDataSource;
	displayedDataSources: `planId:${number}`[];
	selectedProjectData?: ISelectedProject;
	latestFetchedProjectData?: ISelectedProject;
}

export interface ISelectedProject {
	plans: IPlanMetaInProject[];
	id: number;
	name: string;
	description: string;
	status: number;
	type: {
		id: number;
		name: string;
		displayName: string;
	};
	projectTypeId: number;
	typeName: string;
	area: {
		type: 'Polygon';
		coordinates: Array<number[]>[];
	};
	collaboratorIds: number[];
	isDeletable: boolean;
	hasExternalId: boolean;
	createdByUserName: string;
	createdAt: string;
	updatedAt: string;
	companyId: number;
}

export interface IPlanMetaInProject {
	id: number;
	name: string;
	area: {
		type: 'Polygon';
		coordinates: Array<number[]> | [number, number][][];
	};
	description: string;
	type: string;
	czmlMetaData: string;
	createdAt: string;
	updatedAt: string;
	hasAccess: boolean;
	createdByUserName: string;
	highVoltageReportTemplate: { id: number; name: string } | null;
	lowVoltageReportTemplate: { id: number; name: string } | null;
	companyId: number;
	color?: Color;
	status: (typeof projectAndPlanStatus)[number]['value'];
	collaboratorIds: number[];
}

const initialMapModeStore: IMapModeStore = {
	mode: 'LIVE',
	expandableProjectPickerCurrentView: 'MINIMIZED',
	// Mirrors the page.state structure. The difference is that this object represents the map-context (Which project/plan should be shown in map), while page.state represents the UI-state in ExpandablePlanPicker.svelte
	selectedProjectAndPlan: {
		project: {
			id: undefined,
			name: undefined
		},
		plan: {
			id: undefined,
			name: undefined
		}
	},
	settings: {
		planPolygonOpacity: 0.5,
		projectPolygonOpacity: 0,
		entityArea: 2500
	},
	displayedDataSources: [],
	activeDataSource: undefined,
	selectedProjectData: undefined, // Data which is stored whenever a plan is loaded into Cesium.
	latestFetchedProjectData: undefined, // Data which is updated whenever a new project is fetched.
	newProjectPolygonEntity: null,
	newPlanPolygonEntity: null
};

function createMapModeStore() {
	const { subscribe, set, update } = writable<IMapModeStore>(cloneDeep(initialMapModeStore));

	return {
		update,
		set,
		subscribe,
		reset: () =>
			update((store) => {
				store = cloneDeep(initialMapModeStore);
				return store;
			}),
		setInitialProjectsAndPlansState: (
			pickerState:
				| typeof initialMapModeStore.expandableProjectPickerCurrentView
				| undefined = undefined
		) =>
			update((store) => {
				if (pickerState && typeof pickerState !== undefined) {
					store.expandableProjectPickerCurrentView = pickerState;
				}
				store.selectedProjectAndPlan = cloneDeep(initialMapModeStore.selectedProjectAndPlan);
				return store;
			})
	};
}

export const mapModeStore = createMapModeStore();

			
export const getPlanQueryParameter: Readable<string> = derived(
	[mapModeStore, mapLayersStore],
	([$mapModeStore, $mapLayersStore]) => {
		let parameters = [];
		let queryString = '';

		const queryObject = {
			AviationObstacles: false, 
			DistanceControlLineTerrain: false
		};

		if (
			$mapLayersStore.aviationObstaclesBlueDisplay ||
			$mapLayersStore.aviationObstaclesRedDisplay
		) {
			queryObject.AviationObstacles = true;
		} 

		if(get(mapToolsStore).mapStoreInputMode === 'distanceMode'){
			queryObject.DistanceControlLineTerrain = true;
		}

		parameters.push('czmlResponseOptions=' + JSON.stringify(queryObject));
        
		if ($mapModeStore.selectedProjectAndPlan.plan.id) {
			parameters.push('planId=' + $mapModeStore.selectedProjectAndPlan.plan.id);
		}

		parameters.forEach((param, i) => {
			const prefix = i === 0 ? '?' : '&';
			queryString += prefix + param;
		});

		

		return queryString;
	}
);
